import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, Wallet } from 'lucide-react';
import { api } from '@/lib/api';
import { mountAirwallexDropIn } from '@/lib/airwallex';

type PayType = 31 | 100;

const AIRWALLEX_CONTAINER_ID = 'airwallex-account-order-payment-element';

interface OrderItem {
  id: string;
  orderId: string;
  courseId: string;
  title: string;
  amount: number;
  date: string;
  status: 'paid' | 'pending' | 'closed';
}

const OrderPaymentModal = ({
  order,
  onClose,
  onSuccess
}: {
  order: OrderItem;
  onClose: () => void;
  onSuccess: (payload: { orderId: string; orderStatus: number; payTime?: string }) => void;
}) => {
  const [availableAmount, setAvailableAmount] = useState(0);
  const [payType, setPayType] = useState<PayType>(31);
  const [orderPayload, setOrderPayload] = useState<Awaited<ReturnType<typeof api.continueOrderPay>> | null>(null);
  const [dropInReady, setDropInReady] = useState(false);
  const [paying, setPaying] = useState(true);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const dropInRef = useRef<{ destroy?: () => void } | null>(null);

  const canUseBalance = useMemo(() => availableAmount >= order.amount || order.amount === 0, [availableAmount, order.amount]);
  const payOptions = useMemo(() => {
    const options: Array<{ code: PayType; label: string; icon: 'balance' | 'airwallex'; desc: string }> = [];
    if (canUseBalance) {
      options.push({ code: 100, label: '餘額支付', icon: 'balance', desc: '使用帳戶餘額付款' });
    }
    if (order.amount > 0) {
      options.push({ code: 31, label: 'Airwallex', icon: 'airwallex', desc: '信用卡、Google Pay' });
    }
    return options;
  }, [canUseBalance, order.amount]);

  useEffect(() => {
    return () => {
      dropInRef.current?.destroy?.();
    };
  }, []);

  const triggerSuccessClose = () => {
    setShowSuccess(true);
    onClose();
  };

  const refreshOrderPaidStatus = async () => {
    await new Promise((resolve) => window.setTimeout(resolve, 1500));
    await api.getOrderDetail(order.orderId);
  };

  useEffect(() => {
    void (async () => {
      try {
        const latestUser = await api.getCurrentUser();
        const balance = Number(latestUser.availableAmount ?? 0);
        setAvailableAmount(balance);
        const nextPayType: PayType = order.amount > 0 ? 31 : 100;
        setPayType(nextPayType);
        const resp = await api.continueOrderPay({
          orderNo: order.orderId,
          payType: nextPayType
        });
        setOrderPayload(resp);
        if (nextPayType === 100 && resp.orderStatus === 2) {
          onSuccess({ orderId: order.id, orderStatus: 2, payTime: new Date().toISOString() });
          triggerSuccessClose();
        }
      } catch (requestError) {
        setError(requestError instanceof Error ? requestError.message : '初始化付款失敗');
      } finally {
        setPaying(false);
      }
    })();
  }, [order.amount, order.id, order.orderId, onSuccess]);

  useEffect(() => {
    if (!orderPayload?.payMessage || payType !== 31) {
      return;
    }
    let cancelled = false;
    dropInRef.current?.destroy?.();
    setDropInReady(false);
    setPaying(true);

    void mountAirwallexDropIn({
      containerId: AIRWALLEX_CONTAINER_ID,
      payMessage: orderPayload.payMessage,
      locale: 'zh-HK',
      onReady: () => {
        if (!cancelled) {
          setDropInReady(true);
          setPaying(false);
        }
      },
      onCancel: () => {
        if (!cancelled) {
          setPaying(false);
        }
      },
      onError: () => {
        if (!cancelled) {
          setError('支付初始化失敗，請稍後再試');
          setPaying(false);
        }
      },
      onSuccess: () => {
        if (!cancelled) {
          setPaying(true);
          void (async () => {
            try {
              await refreshOrderPaidStatus();
            } catch {
              // Ignore delayed webhook status check failures and still complete.
            }
            onSuccess({
              orderId: order.id,
              orderStatus: 2,
              payTime: new Date().toISOString()
            });
            setPaying(false);
            triggerSuccessClose();
          })();
        }
      }
    }).then((instance) => {
      dropInRef.current = instance;
    }).catch((mountError: unknown) => {
      if (!cancelled) {
        setError(mountError instanceof Error ? mountError.message : '支付初始化失敗');
        setPaying(false);
      }
    });

    return () => {
      cancelled = true;
      dropInRef.current?.destroy?.();
    };
  }, [order.id, orderPayload?.payMessage, onSuccess, payType]);

  const handlePayTypeChange = (nextPayType: PayType) => {
    if (nextPayType === payType || !orderPayload?.orderNo) {
      return;
    }
    if (nextPayType === 100) {
      setPayType(100);
      setError('');
      dropInRef.current?.destroy?.();
      setDropInReady(false);
      return;
    }
    setPaying(true);
    setError('');
    dropInRef.current?.destroy?.();
    void api.continueOrderPay({
      orderNo: orderPayload.orderNo,
      payType: nextPayType
    }).then((resp) => {
      setPayType(nextPayType);
      setOrderPayload(resp);
    }).catch((requestError: unknown) => {
      setError(requestError instanceof Error ? requestError.message : '切換支付方式失敗');
    }).finally(() => {
      setPaying(false);
    });
  };

  const handleBalancePay = () => {
    if (!orderPayload?.orderNo) {
      return;
    }
    setPaying(true);
    setError('');
    void api.continueOrderPay({
      orderNo: orderPayload.orderNo,
      payType: 100
    }).then((resp) => {
      setOrderPayload(resp);
      if (resp.orderStatus === 2) {
        onSuccess({ orderId: order.id, orderStatus: 2, payTime: new Date().toISOString() });
        triggerSuccessClose();
      }
    }).catch((requestError: unknown) => {
      setError(requestError instanceof Error ? requestError.message : '付款失敗');
    }).finally(() => {
      setPaying(false);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={paying ? undefined : onClose} />
      <div className="relative w-full max-w-5xl max-h-[calc(100vh-1.5rem)] overflow-hidden rounded-3xl bg-white shadow-2xl sm:max-h-[calc(100vh-3rem)]">
        {showSuccess ? (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/90">
            <div className="flex flex-col items-center gap-3 rounded-3xl border border-emerald-100 bg-emerald-50 px-10 py-8 text-center shadow-lg">
              <i className="ri-checkbox-circle-line text-4xl text-emerald-500" />
              <p className="text-base font-bold text-emerald-600">付款成功</p>
              <p className="text-xs text-emerald-500/80">正在關閉視窗...</p>
            </div>
          </div>
        ) : null}
        <div className="flex max-h-[calc(100vh-1.5rem)] flex-col lg:flex-row sm:max-h-[calc(100vh-3rem)]">
          <aside className="w-full border-b border-slate-100 bg-slate-50 p-6 lg:w-[300px] lg:border-b-0 lg:border-r">
            <div className="mb-6">
              <p className="text-sm font-bold text-slate-900">課程訂單繼續付款</p>
              <p className="text-[11px] text-slate-400 mt-0.5 font-mono">{order.orderId}</p>
            </div>

            <div className="space-y-3">
              {payOptions.map((option) => (
                <button
                  key={option.code}
                  onClick={() => handlePayTypeChange(option.code)}
                  disabled={paying}
                  className={`flex w-full items-center gap-3 rounded-2xl border px-4 py-4 text-left transition-all ${payType === option.code ? 'border-[#0A1628] bg-[#0A1628] text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'} disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${payType === option.code ? 'bg-white/15' : 'bg-slate-100'}`}>
                    {option.icon === 'balance'
                      ? <Wallet className={`h-5 w-5 ${payType === option.code ? 'text-white' : 'text-slate-500'}`} />
                      : <CreditCard className={`h-5 w-5 ${payType === option.code ? 'text-white' : 'text-slate-500'}`} />}
                  </span>
                  <div>
                    <p className="text-sm font-bold">{option.label}</p>
                    <p className={`text-[11px] ${payType === option.code ? 'text-white/60' : 'text-slate-400'}`}>{option.desc}</p>
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-xs font-semibold text-slate-400">課程訂單</p>
              <p className="mt-2 text-sm font-semibold text-slate-900">{order.title}</p>
              <p className="mt-2 text-2xl font-black text-slate-900">${order.amount}</p>
              <p className="mt-4 text-sm font-semibold text-slate-600">可用餘額</p>
              <p className="mt-1 text-sm font-bold text-slate-900">${availableAmount}</p>
            </div>
          </aside>

          <main className="flex-1 overflow-y-auto p-6 lg:p-8">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <p className="text-2xl font-black text-slate-900">繼續付款</p>
                <p className="mt-1 text-sm text-slate-400">完成付款後即可開通課程學習權限</p>
              </div>
              <button onClick={onClose} disabled={paying} className="flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40">
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            {payType === 31 ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-slate-900">Airwallex 結帳</p>
                    <p className="text-xs text-slate-400">信用卡、Google Pay</p>
                  </div>
                  {!dropInReady ? <span className="text-xs text-slate-400">載入中</span> : null}
                </div>
                <div id={AIRWALLEX_CONTAINER_ID} className="min-h-[320px]" />
              </div>
            ) : (
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-6">
                <p className="text-sm font-bold text-emerald-700">餘額支付</p>
                <p className="mt-2 text-3xl font-black text-slate-900">${availableAmount}</p>
                <p className="mt-2 text-sm text-slate-500">將從你的帳戶餘額中直接扣除本次課程費用。</p>
                <button
                  onClick={handleBalancePay}
                  disabled={paying}
                  className="mt-6 w-full rounded-2xl bg-emerald-600 py-4 text-sm font-bold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60"
                >
                  {paying ? '付款處理中...' : '確認餘額付款'}
                </button>
              </div>
            )}

            {error ? <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600">{error}</div> : null}

            <div className="mt-5 flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
              <i className="ri-shield-check-line text-emerald-500 text-xs" />
              <span>購買後 7 天無理由退款</span>
              <span className="text-slate-300">·</span>
              <Link to="/legal?tab=refund" className="font-medium text-violet-500 underline underline-offset-2 transition-colors hover:text-violet-700">
                查看退款政策
              </Link>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default OrderPaymentModal;
